import { SDKConfig } from "./Config";
import { HttpClient } from "./HttpClient";
import { OnboardingModule } from "../modules/onboarding/onboarding";
import { LivenessModule } from "../modules/liveness/liveness";
import { SDKLogger } from "../utils/logger";
import { normalizeChannels } from "../utils/channels";
import {
  SessionResponse,
  SDKInitConfig,
} from "../types/onboarding";
import { LivenessResumeInfoResponse } from "../types/liveness";

/**
 * SmartComply SDK — main entry point.
 *
 * Usage:
 *   const sdk = new SmartComply({ apiKey: "pk_...", clientId: "uuid-...", environment: "sandbox" });
 *   const session = await sdk.createSession();
 *   const config = await sdk.initializeConfig();
 *   // ... use sdk.onboarding and sdk.liveness modules
 */
export class SmartComply {
  /**
   * Package version, hardcoded to match package.json — bump this alongside
   * every version bump there (same manual-sync convention already used for
   * MEDIAPIPE_VERSION in camera/FaceDetector.ts). Exists so a client's
   * running version can be confirmed directly (SmartComply.VERSION, or the
   * console log on construction below) instead of trusting that whatever
   * CDN tag they installed with (e.g. @1, which floats to the latest
   * matching release) actually resolved to the version they expect.
   */
  static readonly VERSION = "1.0.101";

  private http: HttpClient;
  private clientId: string;
  private _sessionToken: string | null = null;
  private _sessionExpiresAt: string | null = null;
  private _sdkConfig: SDKInitConfig | null = null;

  public onboarding: OnboardingModule;
  public liveness: LivenessModule;

  constructor(config: SDKConfig) {
    if (!config.apiKey) {
      throw new Error("SmartComply: apiKey is required");
    }
    if (!config.clientId) {
      throw new Error("SmartComply: clientId is required (UUID from your SDK config)");
    }

    this.clientId = config.clientId;
    this.http = new HttpClient(config);

    this.onboarding = new OnboardingModule(this.http);
    this.liveness = new LivenessModule(this.http);

    SDKLogger.always(`v${SmartComply.VERSION}`);
  }

  /** Current session token (null if not created yet) */
  get sessionToken(): string | null {
    return this._sessionToken;
  }

  /** Session expiration ISO timestamp */
  get sessionExpiresAt(): string | null {
    return this._sessionExpiresAt;
  }

  /** SDK config fetched from backend (null until initializeConfig() is called) */
  get sdkConfig(): SDKInitConfig | null {
    return this._sdkConfig;
  }

  /**
   * Create a new SDK session.
   *
   * POST /v1/session/create/
   * Auth: Authorization: Bearer <apiKey>
   * Body: { client_id: "<uuid>" }
   *
   * Returns: { token: "...", expires_at: "..." }
   */
  async createSession(): Promise<SessionResponse> {
    const response = await this.http.request<SessionResponse>(
      "POST",
      "/v1/session/create/",
      { client_id: this.clientId }
    );

    this._sessionToken = response.token;
    this._sessionExpiresAt = response.expires_at;

    // Set the session token on the HTTP client for subsequent requests
    this.http.setSessionToken(response.token);

    return response;
  }

  /**
   * Resume an existing session from a resume_token (client-initiated
   * resubmit — see the "Resubmit" action in the Adhere dashboard). Unlike
   * createSession(), this makes no network call: a resume_token IS already
   * a valid SessionToken row on the backend (SDKSessionTokenAuthentication
   * checks it against SessionToken.active the same way as any session
   * token), so there is nothing to create — only to start using directly.
   *
   * Must be used INSTEAD OF createSession(), not alongside it. Calling
   * createSession() after this would mint a second, unrelated session and
   * silently abandon the resume — the backend's resume-match in
   * LivenessCreateAPIView only fires for the session actually presented on
   * the request, so the resume_token has to be the one in use throughout.
   *
   * No expires_at is set here — the resume_token's own issuance response
   * only carries resume_expires_at (a 24h invite-link window), not the
   * session's own runtime expiry the way createSession() returns one. The
   * backend still enforces the real expiry server-side regardless; this
   * only means the client-side "session about to expire" warning timer
   * (see SmartComplyFlow.initialize()) has nothing to schedule against and
   * is skipped for a resumed session.
   */
  resumeSession(resumeToken: string): void {
    this._sessionToken = resumeToken;
    this._sessionExpiresAt = null;
    this.http.setSessionToken(resumeToken);
  }

  /**
   * Fetch SDK configuration from the backend.
   * Must be called after createSession() or resumeSession().
   *
   * GET /v1/initialize/
   * Auth: x-access-token: <sessionToken>
   *
   * Returns: { brand_name, theme, verification_type, channels, redirect_url, ... }
   */
  async initializeConfig(): Promise<SDKInitConfig> {
    const response = await this.http.sessionRequest<SDKInitConfig>(
      "GET",
      "/v1/initialize/"
    );
    // Older backends send one flat channel list per country; always hand
    // callers the {data_verification, document_verification} shape.
    const config = { ...response, channels: normalizeChannels(response.channels) };

    this._sdkConfig = config;
    return config;
  }

  /**
   * Resolve a resume_token to the entry/step it's resuming, without
   * consuming the single-use resume binding (unlike create/, which does).
   * Only meaningful after resumeSession() — a normal createSession()
   * session was never issued for a resume, so this returns a 404
   * (NOT_A_RESUME_TOKEN) for it, same as the backend.
   *
   * GET /v1/sdk/liveness/resume-info/
   * Auth: x-access-token: <sessionToken>
   */
  async getResumeInfo(): Promise<LivenessResumeInfoResponse> {
    return this.http.sessionRequest<LivenessResumeInfoResponse>(
      "GET",
      "/v1/sdk/liveness/resume-info/"
    );
  }
}
